/**
 * ValoraaBead wishlist page (<vb-wishlist>, sections/vb-wishlist.liquid).
 *
 * - Renders the saved handles (assets/vb-wishlist.js, newest first) as the shop's cards through
 *   the Section Rendering API: /products/<handle>?section_id=vb-wishlist-item.
 * - Shared list: /pages/wishlist?items=a,b shows those pieces on any device, with
 *   "Save these to my wishlist" (adds them to this device's list) and "View my wishlist".
 * - Add all to bag: one /cart/add.js call with every in-stock saved piece, each with the option
 *   chosen on its card (default: first available), through Horizon's cart protocol
 *   (CartLinesUpdateEvent), so the drawer, bubble and free-shipping bar update and the drawer opens.
 * - Share list: the system share sheet where there is one, else the link is copied.
 * - Remove: the card turns into "Removed. Undo" for 5 s; Undo puts it back where it was.
 * - Price drop: "Now ₹X (was ₹Y when you saved it)" only when the current price is lower than
 *   the price stored when the piece was saved. Nothing else (no urgency, no stock counts).
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const KEY = 'vb:wishlist';
const UNDO_MS = 5000;

const readLocal = () => {
  try {
    const list = JSON.parse(localStorage.getItem(KEY) || '[]');
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
};
const store = () => /** @type {any} */ (window).vbWishlist;
const read = () => (store() ? store().read() : readLocal());

const money = (/** @type {number} */ cents) => {
  const currency = /** @type {any} */ (window).Shopify?.currency?.active || 'INR';
  try {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 0 }).format(cents / 100);
  } catch {
    return String(Math.round(cents / 100));
  }
};

class VbWishlist extends HTMLElement {
  #abort = new AbortController();
  #token = 0;
  /** @type {string[] | null} handles from ?items= (a shared list), or null for this device's list */
  #shared = null;
  /** @type {Map<Element, number>} */
  #undoTimers = new Map();

  connectedCallback() {
    this.grid = this.querySelector('[data-vb-wl-grid]');
    this.empty = this.querySelector('[data-vb-wl-empty]');
    this.loading = this.querySelector('[data-vb-wl-loading]');
    this.status = this.querySelector('[data-vb-wl-status]');
    this.count = this.querySelector('[data-vb-wl-count]');
    this.actions = this.querySelector('[data-vb-wl-actions]');
    this.sharedActions = this.querySelector('[data-vb-wl-shared-actions]');

    const items = new URL(window.location.href).searchParams.get('items');
    if (items) {
      this.#shared = [...new Set(items.split(',').map((h) => h.trim().toLowerCase()).filter((h) => /^[a-z0-9][a-z0-9-]*$/.test(h)))].slice(0, 50);
    }

    const { signal } = this.#abort;
    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    document.addEventListener('vb:wishlist:change', () => this.#sync(), { signal });
    this.render();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#abort = new AbortController();
  }

  /** @returns {string[]} */
  #list() {
    return this.#shared ?? [...read()].reverse();
  }

  async render() {
    const token = ++this.#token;
    const list = this.#list();
    this.#mode();
    if (list.length === 0) {
      this.#show([]);
      return;
    }
    this.loading.hidden = false;
    const base = this.dataset.itemUrl || '/products/';
    const html = await Promise.all(
      list.map((handle) =>
        fetch(`${base}${encodeURIComponent(handle)}?section_id=vb-wishlist-item`, { headers: { Accept: 'text/html' } })
          .then((response) => (response.ok ? response.text() : null))
          .catch(() => undefined)
      )
    );
    if (token !== this.#token) return;
    const items = [];
    const gone = [];
    html.forEach((text, index) => {
      const item = text ? new DOMParser().parseFromString(text, 'text/html').querySelector('[data-vb-wl-item]') : null;
      if (item) items.push(document.importNode(item, true));
      else if (text === null) gone.push(list[index]);
    });
    // Products that no longer exist leave this device's list quietly
    if (!this.#shared && gone.length && store()) store().write(read().filter((h) => !gone.includes(h)));
    this.#show(items);
  }

  /** Shared list vs. this device's list: which line and buttons show. */
  #mode() {
    const shared = Boolean(this.#shared);
    const helper = this.querySelector('[data-vb-wl-helper]');
    const sharedLine = this.querySelector('[data-vb-wl-shared-line]');
    if (helper) helper.hidden = shared;
    if (sharedLine) sharedLine.hidden = !shared;
  }

  /** @param {Element[]} items */
  #show(items) {
    this.loading.hidden = true;
    if (this.#shared) {
      for (const item of items) item.querySelector('[data-vb-wl-remove]')?.remove();
    }
    this.grid.replaceChildren(...items);
    this.#priceDrops();
    this.#refresh();
    store()?.paint(this);
  }

  /** Count, empty state and which buttons show. */
  #refresh() {
    const shown = this.grid.querySelectorAll('[data-vb-wl-item]:not([data-removed])').length;
    this.empty.hidden = shown > 0 || Boolean(this.#shared);
    if (this.count) {
      const prefix = this.#shared ? 'data-shared-' : 'data-';
      const one = this.count.getAttribute(`${prefix}one`) || '';
      const other = this.count.getAttribute(`${prefix}other`) || '';
      this.count.textContent = shown === 0 ? '' : (shown === 1 ? one : other).replace('[count]', String(shown));
    }
    if (this.actions) this.actions.hidden = shown === 0 || Boolean(this.#shared);
    if (this.sharedActions) this.sharedActions.hidden = !this.#shared || shown === 0;
    const addAll = this.querySelector('[data-vb-wl-add-all]');
    if (addAll instanceof HTMLButtonElement) {
      addAll.disabled = this.grid.querySelectorAll('[data-vb-wl-item][data-available]:not([data-removed])').length === 0;
    }
  }

  /** "Now ₹X (was ₹Y when you saved it)", only when the price really is lower now. */
  #priceDrops() {
    if (this.#shared) return;
    const saved = store()?.readPrices?.() ?? {};
    for (const item of this.grid.querySelectorAll('[data-vb-wl-item]')) {
      const line = item.querySelector('[data-vb-wl-drop]');
      const was = Number(saved[item.getAttribute('data-vb-wl-item') || '']);
      const now = Number(item.getAttribute('data-price'));
      if (!(line instanceof HTMLElement) || !was || !now || now >= was) continue;
      line.textContent = (line.dataset.template || '').replace('[now]', line.dataset.now || money(now)).replace('[was]', money(was));
      line.hidden = false;
    }
  }

  /** Another tab or a heart elsewhere changed this device's list. */
  #sync() {
    if (this.#shared) return;
    const list = read();
    const shown = [...this.grid.querySelectorAll('[data-vb-wl-item]:not([data-removed])')];
    const missing = list.some((h) => !shown.some((item) => item.getAttribute('data-vb-wl-item') === h));
    if (missing) {
      this.render();
      return;
    }
    for (const item of shown) {
      if (!list.includes(item.getAttribute('data-vb-wl-item') || '')) item.remove();
    }
    this.#refresh();
  }

  /** @param {string} text */
  #say(text) {
    if (!this.status) return;
    this.status.textContent = '';
    requestAnimationFrame(() => (this.status.textContent = text));
  }

  /** @param {MouseEvent} event */
  #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const remove = target.closest('[data-vb-wl-remove]');
    if (remove) return this.#remove(remove);
    const undo = target.closest('[data-vb-wl-undo-btn]');
    if (undo) return this.#undo(undo.closest('[data-vb-wl-item]'));
    if (target.closest('[data-vb-wl-add-all]')) return this.#addAll(/** @type {HTMLButtonElement} */ (target.closest('button')));
    if (target.closest('[data-vb-wl-share]')) return this.#share(/** @type {HTMLButtonElement} */ (target.closest('button')));
    if (target.closest('[data-vb-wl-save-shared]')) return this.#saveShared(/** @type {HTMLButtonElement} */ (target.closest('button')));
  }

  /** @param {Element} button */
  #remove(button) {
    const item = button.closest('[data-vb-wl-item]');
    if (!item) return;
    const handle = item.getAttribute('data-vb-wl-item') || '';
    const index = read().indexOf(handle);
    item.setAttribute('data-removed', '');
    item.setAttribute('data-index', String(index));
    const body = item.querySelector('.vb-wl-item__body');
    const undoBox = item.querySelector('[data-vb-wl-undo]');
    if (body instanceof HTMLElement) body.hidden = true;
    if (undoBox instanceof HTMLElement) undoBox.hidden = false;
    store()?.remove(handle);
    this.#say(button.getAttribute('data-status') || '');
    item.querySelector('[data-vb-wl-undo-btn]')?.focus();
    this.#undoTimers.set(
      item,
      window.setTimeout(() => {
        this.#undoTimers.delete(item);
        const next = item.nextElementSibling?.querySelector('a[href]') || item.previousElementSibling?.querySelector('a[href]');
        const hadFocus = item.contains(document.activeElement);
        item.remove();
        this.#refresh();
        if (hadFocus && next instanceof HTMLElement) next.focus();
      }, UNDO_MS)
    );
    this.#refresh();
  }

  /** @param {Element | null} item */
  #undo(item) {
    if (!item) return;
    clearTimeout(this.#undoTimers.get(item));
    this.#undoTimers.delete(item);
    const handle = item.getAttribute('data-vb-wl-item') || '';
    const index = Number(item.getAttribute('data-index'));
    item.removeAttribute('data-removed');
    const body = item.querySelector('.vb-wl-item__body');
    const undoBox = item.querySelector('[data-vb-wl-undo]');
    if (body instanceof HTMLElement) body.hidden = false;
    if (undoBox instanceof HTMLElement) undoBox.hidden = true;
    if (store()) store().insertAt(handle, Number.isFinite(index) && index >= 0 ? index : read().length);
    item.querySelector('[data-vb-wl-remove]')?.focus();
    this.#refresh();
  }

  /** @param {HTMLButtonElement | null} button */
  async #addAll(button) {
    const forms = [...this.grid.querySelectorAll('[data-vb-wl-item][data-available]:not([data-removed]) form[data-vb-wl-form]')];
    const items = forms
      .map((form) => {
        const id = new FormData(/** @type {HTMLFormElement} */ (form)).get('id');
        return id ? { id: Number(id), quantity: 1 } : null;
      })
      .filter(Boolean);
    if (items.length === 0) {
      this.#say(this.dataset.none || '');
      return;
    }
    const label = button?.querySelector('.vb-btn__label');
    const original = label?.textContent || '';
    if (button) button.disabled = true;
    if (label) label.textContent = this.dataset.adding || original;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((line) => ({ merchandiseId: String(line?.id), quantity: 1 })),
        promise: deferred.promise,
      })
    );
    try {
      const response = await fetch(`${this.dataset.cartAddUrl || '/cart/add'}.js`.replace('.js.js', '.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const result = await response.json();
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: Boolean(result.status), items: cart.items, sections: result.sections, source: 'vb-wishlist', itemCount: items.length },
      });
      this.#say(result.status ? result.description || result.message || '' : this.dataset.added || '');
    } catch (error) {
      deferred.reject(error);
    } finally {
      if (button) button.disabled = false;
      if (label) label.textContent = original;
    }
  }

  /** @param {HTMLButtonElement | null} button */
  async #share(button) {
    const handles = [...read()].reverse();
    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set('items', handles.join(','));
    const link = url.toString();
    try {
      if (navigator.share && window.matchMedia('(pointer: coarse)').matches) {
        await navigator.share({ title: this.dataset.shareTitle || document.title, url: link });
        return;
      }
      await navigator.clipboard.writeText(link);
    } catch {
      // Clipboard blocked: select the link in a prompt-free way by putting it in the address bar
      window.history.replaceState(window.history.state, '', link);
    }
    const label = button?.querySelector('.vb-btn__label');
    if (label) {
      const original = label.textContent;
      label.textContent = this.dataset.copied || original;
      setTimeout(() => (label.textContent = original), 2200);
    }
    this.#say(this.dataset.copied || '');
  }

  /** @param {HTMLButtonElement | null} button */
  #saveShared(button) {
    if (!this.#shared || !store()) return;
    store().addMany([...this.#shared].reverse());
    const label = button?.querySelector('.vb-btn__label');
    if (label) label.textContent = this.dataset.savedShared || label.textContent;
    if (button) button.disabled = true;
    this.#say(this.dataset.savedShared || '');
  }
}

if (!customElements.get('vb-wishlist')) customElements.define('vb-wishlist', VbWishlist);
